// Coupon management redesign (Phase 2 PR 4, 2026-05-15):
// coupon_templates CRUD MobX store. 既存 LandmarkStore pattern 踏襲。

import { CouponTemplateApi } from '@/apis';
import { ResponseData } from '@/types/http';
import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';

export default class CouponTemplateStore extends BaseStore {
    templates: any[] = [];
    template: any = null;
    total: number = 0;
    api: CouponTemplateApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            templates: observable,
            template: observable,
            total: observable,
            list: flow.bound,
            getById: flow.bound,
            create: flow.bound,
            update: flow.bound,
            retranslate: flow.bound,
            remove: flow.bound,
            // Phase 2 PR 5 (2026-05-15): batch 入稿 + code 一覧 (一覧 UI は PR 6 で実装)
            addCodes: flow.bound,
            listCodes: flow.bound,
            // Phase 2 PR 7 (2026-05-15): CSV エクスポート (= POS マスタ用 dump)
            exportCodes: flow.bound,
            // クーポン画像 (2026-07-02): 画像アップロード (= POST /system/file)
            uploadImage: flow.bound
        });
        this.api = new CouponTemplateApi();
    }

    *list(searchParams?: { landmark_id?: number | string }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.list, searchParams);
            if (res?.ok) {
                this.templates = res.data?.items ?? [];
                this.total = res.data?.total ?? 0;
            }
            return res;
        } catch (_e) {
            return null;
        }
    }

    *getById(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getById, id);
            if (res?.ok) {
                this.template = res.data;
            }
            return res;
        } catch (_e) {
            return null;
        }
    }

    *create(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.create, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *update(payload: { id: string | number, data: any }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.update, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    // 民泊 PoC Step 3: 「翻訳を作り直す」。失敗しても例外にせず response (data.translation.ok=false) を返す。
    *retranslate(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.retranslate, id, { hideLoading: true });
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *remove(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.remove, id);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    // Phase 2 PR 5 (2026-05-15)
    *addCodes(payload: {
        id: string | number,
        data: { codes: string[], default_valid_to?: string | null, on_duplicate?: 'skip' | 'reject' | 'error' }
    }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.addCodes, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *listCodes(payload: {
        id: string | number,
        params?: { state?: string, limit?: number, offset?: number }
    }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.listCodes, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    // Phase 2 PR 7 (2026-05-15): CSV ダウンロード。
    // ApiStore.call が Content-Type text/csv を検知して FileSaver で自動 download する
    // (= src/utils/file.ts handleSaveFile)。本 method は call を呼ぶだけで OK。
    *exportCodes(payload: { id: string | number }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.exportCodesAsBlob, payload.id);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    // クーポン画像 (2026-07-02): 画像アップロード。res.data.path が配信 URL。
    // Spec: design/coupon-image-feature-spec-20260702.md
    *uploadImage(payload: { file: File }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.uploadImage, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }
}
